import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, Users, Calendar, IndianRupee, TrendingUp, ShieldAlert, Shield, LogOut, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { admin as adminApi, auth } from '../lib/api';
import { formatINR, formatDate, clearSession } from '../lib/auth';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [stats, setStats] = React.useState(null);
  const [recentBookings, setRecentBookings] = React.useState([]);
  const [recentUsers, setRecentUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState(null);

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, bookingsRes, usersRes] = await Promise.all([
        adminApi.stats(),
        adminApi.bookings({ per_page: 10 }),
        adminApi.users({ per_page: 10 }),
      ]);
      setStats(statsRes.data);
      setRecentBookings(bookingsRes.data.data || []);
      setRecentUsers(usersRes.data.data || []);
    } catch (err) {
      toast.error('Could not load admin data.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch {
      // ignore
    }
    clearSession();
    navigate('/auth');
  };

  const handleToggleVerify = async (photographerProfileId, verified) => {
    setBusyId(photographerProfileId);
    try {
      await adminApi.verifyPhotographer(photographerProfileId, verified);
      toast.success(verified ? 'Photographer verified.' : 'Verification revoked.');
      loadAll();
    } catch {
      toast.error('Could not update verification status.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading && !stats) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest Admin</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Badge variant="secondary" data-testid="admin-badge">
                <Shield className="w-3 h-3 mr-1" />
                Admin
              </Badge>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Log Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-2" data-testid="dashboard-title">Admin Dashboard</h1>
          <p className="text-lg text-muted-foreground" data-testid="dashboard-subtitle">Manage users, bookings, and platform analytics</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-testid="stats-grid">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">Total Revenue <IndianRupee className="w-4 h-4" /></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold mb-1" data-testid="stat-revenue">{formatINR(stats?.total_revenue)}</div>
              <div className="text-xs text-muted-foreground flex items-center"><TrendingUp className="w-3 h-3 mr-1" />{stats?.completed_bookings || 0} completed bookings</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">Total Users <Users className="w-4 h-4" /></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold" data-testid="stat-users">{stats?.total_users}</div>
              <div className="text-xs text-muted-foreground">{stats?.total_photographers} photographers · {stats?.total_clients} clients</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">Total Bookings <Calendar className="w-4 h-4" /></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold" data-testid="stat-bookings">{stats?.total_bookings}</div>
              <div className="text-xs text-muted-foreground">{stats?.bookings_this_month} this month</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">Pending Verifications <ShieldAlert className="w-4 h-4" /></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold" data-testid="stat-pending">{stats?.pending_photographers}</div>
              <div className="text-xs text-muted-foreground">{stats?.verified_photographers} verified</div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Bookings */}
          <Card data-testid="recent-bookings">
            <CardHeader>
              <CardTitle>Recent Bookings</CardTitle>
              <CardDescription>Latest transactions on the platform</CardDescription>
            </CardHeader>
            <CardContent>
              {recentBookings.length === 0 && <p className="text-sm text-muted-foreground">No bookings yet.</p>}
              <div className="space-y-4">
                {recentBookings.map((booking) => (
                  <div key={booking.id} className="flex items-center justify-between py-3 border-b last:border-0" data-testid={`booking-${booking.id}`}>
                    <div className="flex-1">
                      <div className="font-medium text-sm" data-testid={`booking-client-${booking.id}`}>{booking.client?.name}</div>
                      <div className="text-xs text-muted-foreground" data-testid={`booking-photographer-${booking.id}`}>with {booking.photographer?.business_name}</div>
                      <div className="text-xs text-muted-foreground mt-1" data-testid={`booking-date-${booking.id}`}>{formatDate(booking.event_date)}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold" data-testid={`booking-amount-${booking.id}`}>{formatINR(booking.total_amount)}</div>
                      <Badge variant={booking.status === 'completed' ? 'default' : booking.status === 'cancelled' ? 'destructive' : 'secondary'} className="text-xs capitalize" data-testid={`booking-status-${booking.id}`}>
                        {booking.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Users */}
          <Card data-testid="recent-users">
            <CardHeader>
              <CardTitle>Recent Users</CardTitle>
              <CardDescription>New registrations and photographer verification</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentUsers.map((u) => (
                  <div key={u.id} className="flex items-center justify-between py-3 border-b last:border-0" data-testid={`user-${u.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <Users className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-medium text-sm flex items-center gap-1" data-testid={`user-name-${u.id}`}>
                          {u.name}
                          {u.photographer_profile?.is_verified && <BadgeCheck className="w-3 h-3 text-primary" />}
                        </div>
                        <div className="text-xs text-muted-foreground capitalize" data-testid={`user-type-${u.id}`}>{u.role}</div>
                        <div className="text-xs text-muted-foreground" data-testid={`user-joined-${u.id}`}>Joined {formatDate(u.created_at)}</div>
                      </div>
                    </div>
                    {u.role === 'photographer' && u.photographer_profile ? (
                      <Button
                        size="sm"
                        variant={u.photographer_profile.is_verified ? 'outline' : 'default'}
                        disabled={busyId === u.photographer_profile.id}
                        onClick={() => handleToggleVerify(u.photographer_profile.id, !u.photographer_profile.is_verified)}
                        data-testid={`user-status-${u.id}`}
                      >
                        {u.photographer_profile.is_verified ? 'Unverify' : 'Verify'}
                      </Button>
                    ) : (
                      <Badge variant="secondary" data-testid={`user-status-${u.id}`}>{u.role}</Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
